import { ComponentRef, FormEvent, ReactElement, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  ErrorSummary,
  Field,
  Heading,
  Label,
  Paragraph,
  Select,
  Tag,
} from '@digdir/designsystemet-react';
import { ShieldLockIcon } from '@navikt/aksel-icons';
import type { FactorKind, PublicWorkEnvironmentSurvey } from '../../api/workEnvironmentTypes';
import { useGetWorkEnvironmentSurveyByUUIDQuery } from '../../hooks/queries/useGetWorkEnvironmentSurveyByUUIDQuery';
import { useSubmitWorkEnvironmentSurveyMutation } from '../../hooks/mutations/useSubmitWorkEnvironmentSurveyMutation';
import { useLanguageSynchronization } from '../../hooks/useLanguageSynchronization';
import { FormSubmissionUtils } from './formSubmission.utils.ts';
import { QuestionCard } from './components/QuestionCard';
import { SensitivePartNotice } from './components/SensitivePartNotice';
import { SurveyClosed } from './components/SurveyClosed';
import { SurveyHeader } from './components/SurveyHeader';
import { SurveyThankYou } from './components/SurveyThankYou';
import {
  Answers,
  answeredCount,
  isPartDone,
  missingInPart,
  questionCount,
  questionElementId,
  questionLanguageFor,
  surveyResponse,
} from './workEnvironmentFormView';
import classes from './WorkEnvironmentFormSubmission.module.css';

const KIND_COLORS: Record<FactorKind, 'accent' | 'neutral'> = {
  required: 'accent',
  standard: 'neutral',
  custom: 'neutral',
};

export const WorkEnvironmentFormSubmission = (): ReactElement | null => {
  const { i18n } = useTranslation();
  const { guid } = useParams();
  const { handleLanguageChange } = useLanguageSynchronization();
  const [chosenLanguage, setChosenLanguage] = useState<string | null>(null);
  const questionLanguage = chosenLanguage ?? questionLanguageFor(i18n.language);
  const { data: survey, isLoading, isError } = useGetWorkEnvironmentSurveyByUUIDQuery(guid, questionLanguage);
  const submit = useSubmitWorkEnvironmentSurveyMutation();
  // The questions stay in the language that loaded, and the employee can try the other one again
  const [failedLanguage, setFailedLanguage] = useState<string | null>(null);
  useEffect(() => {
    if (isError && survey && chosenLanguage && chosenLanguage !== survey.questionnaire.language) {
      setFailedLanguage(chosenLanguage);
      setChosenLanguage(survey.questionnaire.language);
    }
  }, [isError, survey, chosenLanguage]);

  if (isError && !survey) return <SurveyClosed reason="loadFailed" onLanguageChange={handleLanguageChange} />;
  if (!guid || isLoading || !survey) return null;
  if (!submit.isSuccess && (!survey.isActive || FormSubmissionUtils.hasAnswered(survey.uuid))) {
    // Answers are anonymous, so only this browser knows it answered. Otherwise the survey has closed.
    const reason = FormSubmissionUtils.hasAnswered(survey.uuid) ? 'alreadyAnswered' : 'expired';
    return <SurveyClosed reason={reason} onLanguageChange={handleLanguageChange} />;
  }

  return (
    <SurveyForm
      survey={survey}
      languageFailed={failedLanguage !== null}
      onQuestionLanguageChange={(language) => {
        setFailedLanguage(null);
        setChosenLanguage(language);
      }}
      onLanguageChange={handleLanguageChange}
      submit={{
        run: (response) =>
          submit.mutate(response, { onSuccess: () => FormSubmissionUtils.addNewAnsweredSurvey(survey.uuid) }),
        pending: submit.isPending,
        done: submit.isSuccess,
        failed: submit.isError,
      }}
    />
  );
};

type SurveyFormProps = {
  survey: PublicWorkEnvironmentSurvey;
  /** The questions could not be loaded in the language the employee chose */
  languageFailed: boolean;
  onQuestionLanguageChange: (language: string) => void;
  onLanguageChange: (language: string) => void;
  submit: {
    run: (response: ReturnType<typeof surveyResponse>) => void;
    pending: boolean;
    done: boolean;
    failed: boolean;
  };
};

const SurveyForm = ({
  survey,
  languageFailed,
  onQuestionLanguageChange,
  onLanguageChange,
  submit,
}: SurveyFormProps): ReactElement => {
  const { t } = useTranslation();
  const { questionnaire } = survey;
  const [partIndex, setPartIndex] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [skipped, setSkipped] = useState<string[]>([]);
  const [tried, setTried] = useState(false);
  const [triedCount, setTriedCount] = useState(0);
  const mainRef = useRef<HTMLElement>(null);
  const summaryRef = useRef<ComponentRef<typeof ErrorSummary>>(null);

  const parts = questionnaire.factors;
  const part = parts[partIndex];
  const isLast = partIndex === parts.length - 1;
  const missing = missingInPart(part, answers, skipped);
  const total = questionCount(questionnaire);
  const answered = answeredCount(questionnaire, answers);

  useEffect(() => {
    if (triedCount > 0) summaryRef.current?.focus();
  }, [triedCount]);

  useEffect(() => {
    window.scrollTo(0, 0);
    mainRef.current?.focus({ preventScroll: true });
  }, [partIndex, submit.done]);

  const goTo = (index: number) => {
    setPartIndex(index);
    setTried(false);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (missing.length > 0) {
      setTried(true);
      setTriedCount((count) => count + 1);
      return;
    }
    if (isLast) submit.run(surveyResponse(survey.uuid, questionnaire, answers, skipped));
    else goTo(partIndex + 1);
  };

  const setSkippedPart = (skip: boolean) =>
    setSkipped((current) => (skip ? [...current, part.key] : current.filter((key) => key !== part.key)));

  return (
    <div className={classes.page} data-size="md">
      <a href="#main" className={`ds-link ${classes.skipLink}`}>
        {t('workEnvironmentFormSubmission.skipToQuestions')}
      </a>
      <SurveyHeader
        progress={submit.done ? 100 : total > 0 ? (answered / total) * 100 : 0}
        onLanguageChange={onLanguageChange}
      />
      <main id="main" ref={mainRef} tabIndex={-1} className={classes.main}>
        <div aria-hidden="true" className={classes.backdrop} />
        {submit.done ? (
          <SurveyThankYou />
        ) : (
          <div className={classes.content}>
            <div className={classes.intro}>
              <span className={`ds-label ${classes.eyebrow}`} data-size="sm">
                {t('workEnvironmentFormSubmission.eyebrow')}
              </span>
              <Heading level={1} data-size="xl" className={classes.title}>
                {survey.title}
              </Heading>
              {partIndex === 0 && (
                <>
                  <Paragraph className={classes.introText}>{t('workEnvironmentFormSubmission.intro')}</Paragraph>
                  <div className={classes.anonymous}>
                    <ShieldLockIcon aria-hidden />
                    <Paragraph data-size="sm">{t('workEnvironmentFormSubmission.anonymous')}</Paragraph>
                  </div>
                  {questionnaire.languages.length > 1 && (
                    <Field data-size="sm" className={classes.questionLanguage}>
                      <Label htmlFor="question-language">{t('workEnvironmentFormSubmission.questionLanguage')}</Label>
                      <Select
                        id="question-language"
                        value={questionnaire.language}
                        onChange={(event) => onQuestionLanguageChange(event.target.value)}
                      >
                        {questionnaire.languages.map((code) => (
                          <Select.Option key={code} value={code}>
                            {t(`hms.languages.${code}`, code)}
                          </Select.Option>
                        ))}
                      </Select>
                    </Field>
                  )}
                  {languageFailed && (
                    <Alert data-color="warning" data-size="sm">
                      <Paragraph data-size="sm">{t('workEnvironmentFormSubmission.languageFailed')}</Paragraph>
                    </Alert>
                  )}
                </>
              )}
              <div className={classes.parts}>
                <div
                  aria-hidden="true"
                  className={classes.segments}
                  style={{ gridTemplateColumns: `repeat(${parts.length}, minmax(0, 1fr))` }}
                >
                  {parts.map((factor, index) => (
                    <span
                      key={factor.key}
                      data-state={
                        index === partIndex ? 'current' : isPartDone(factor, answers, skipped) ? 'done' : undefined
                      }
                    />
                  ))}
                </div>
                <Paragraph data-size="sm" className={classes.subtle}>
                  {t('workEnvironmentFormSubmission.partOf', {
                    current: partIndex + 1,
                    total: parts.length,
                    name: part.name,
                  })}
                </Paragraph>
              </div>
            </div>

            <form
              noValidate
              onSubmit={onSubmit}
              className={classes.form}
              lang={questionnaire.language}
              dir={questionnaire.direction}
            >
              {tried && missing.length > 0 && (
                <ErrorSummary ref={summaryRef}>
                  <ErrorSummary.Heading>{t('workEnvironmentFormSubmission.errorSummary')}</ErrorSummary.Heading>
                  <ErrorSummary.List>
                    {missing.map((question) => (
                      <ErrorSummary.Item key={question.ref}>
                        <ErrorSummary.Link href={`#${questionElementId(question.ref)}`}>
                          {question.text}
                        </ErrorSummary.Link>
                      </ErrorSummary.Item>
                    ))}
                  </ErrorSummary.List>
                </ErrorSummary>
              )}

              <section aria-labelledby={`part-${part.key}`} className={classes.part}>
                <div className={classes.partHeading}>
                  <span className={classes.partMeta}>
                    <span className={`ds-label ${classes.partNumber}`} data-size="sm">
                      {t('workEnvironmentFormSubmission.part', { current: partIndex + 1, total: parts.length })}
                    </span>
                    <Tag data-size="sm" data-color={KIND_COLORS[part.kind]}>
                      {t(`workEnvironmentFormSubmission.kinds.${part.kind}`)}
                    </Tag>
                  </span>
                  <Heading level={2} data-size="md" id={`part-${part.key}`}>
                    {part.name}
                  </Heading>
                </div>
                {part.sensitive && (
                  <SensitivePartNotice
                    factor={part}
                    channels={survey}
                    skipped={skipped.includes(part.key)}
                    onSkippedChange={setSkippedPart}
                  />
                )}
                {!skipped.includes(part.key) &&
                  part.questions.map((question, index) => (
                    <QuestionCard
                      key={question.ref}
                      question={question}
                      number={index + 1}
                      total={part.questions.length}
                      value={answers[question.ref]}
                      onChange={(value) => setAnswers((current) => ({ ...current, [question.ref]: value }))}
                      invalid={tried && answers[question.ref] === undefined}
                    />
                  ))}
              </section>

              {submit.failed && (
                <Alert data-color="danger">
                  <Paragraph>{t('workEnvironmentFormSubmission.submitFailed')}</Paragraph>
                </Alert>
              )}

              <div className={classes.footer}>
                {partIndex > 0 && (
                  <Button variant="secondary" data-size="lg" onClick={() => goTo(partIndex - 1)}>
                    {t('workEnvironmentFormSubmission.back')}
                  </Button>
                )}
                <Paragraph data-size="sm" role="status" className={classes.subtle}>
                  {t('workEnvironmentFormSubmission.answered', { answered, total })}
                </Paragraph>
                <Button
                  type="submit"
                  variant="primary"
                  data-size="lg"
                  className={classes.nextButton}
                  loading={submit.pending}
                >
                  {isLast ? t('workEnvironmentFormSubmission.send') : t('workEnvironmentFormSubmission.next')}
                </Button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};
